import { XMLBuilder, XMLParser } from "fast-xml-parser"
import type { ParameterEntry } from "@/types/parameter"

const xmlParser = new XMLParser({
  ignoreAttributes: false,
  parseTagValue: false,
  isArray: (name) =>
    name === "Parameter" ||
    name === "Value",
})

const xmlBuilder = new XMLBuilder({
  ignoreAttributes: false,
  format: true,
})

const CANONICAL_PARAMETER_NAMES: Record<string, string> = {
  udvalculturename: "udValCultureName",
  udvalheadingshading: "udValHeadingShading",
}

function getCanonicalParameterName(name: string): string {
  return CANONICAL_PARAMETER_NAMES[name.toLowerCase()] ?? name
}

/**
 * Normalizes parameter entries so names are treated case-insensitively.
 * If duplicate names exist with different casing, the last entry wins.
 */
function normalizeEntries(entries: ParameterEntry[]): ParameterEntry[] {
  const normalized: ParameterEntry[] = []
  const indexByLowerName = new Map<string, number>()
  const hasCanonicalName = new Map<string, boolean>()

  for (const entry of entries) {
    const canonicalName = getCanonicalParameterName(entry.parameterName)
    const lowerName = canonicalName.toLowerCase()
    const isCanonical = entry.parameterName === canonicalName
    const nextEntry: ParameterEntry = {
      parameterName: canonicalName,
      selectedValues: entry.selectedValues,
    }

    const existingIndex = indexByLowerName.get(lowerName)
    if (existingIndex === undefined) {
      indexByLowerName.set(lowerName, normalized.length)
      hasCanonicalName.set(lowerName, isCanonical)
      normalized.push(nextEntry)
      continue
    }

    // For canonical names, keep canonical-cased source data once seen.
    // Non-canonical duplicates must not replace an existing canonical entry.
    if (hasCanonicalName.get(lowerName) && !isCanonical) {
      continue
    }

    hasCanonicalName.set(lowerName, isCanonical || !!hasCanonicalName.get(lowerName))
    normalized[existingIndex] = nextEntry
  }

  return normalized
}

/** Serializes an empty parameter array to valid XML matching the C# backend format */
export function serializeEmptyParameters(): string {
  return '<?xml version="1.0" encoding="utf-16"?><ArrayOfParameter xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema" />'
}

export function serializeParameters(entries: ParameterEntry[]): string {
  const normalizedEntries = normalizeEntries(entries)
  if (normalizedEntries.length === 0) return serializeEmptyParameters()

  const data = {
    ArrayOfParameter: {
      Parameter: normalizedEntries.map((e) => ({
        ParameterName: e.parameterName,
        SelectedValues: {
          Value: e.selectedValues,
        },
      })),
    },
  }

  return xmlBuilder.build(data)
}

export function deserializeParameters(xml: string): ParameterEntry[] {
  if (!xml) return []

  try {
    const parsed = xmlParser.parse(xml)

    // Primary format: ArrayOfParameter/Parameter (matches C# [XmlType("Parameter")])
    // Fallback: ArrayOfParameterEntry/ParameterEntry (legacy records — remove once DB is migrated)
    const entries =
      parsed?.ArrayOfParameter?.Parameter ||
      parsed?.ArrayOfParameterEntry?.ParameterEntry ||
      []
    const entryArray = Array.isArray(entries) ? entries : [entries]

    const mappedEntries = entryArray.map((e: Record<string, unknown>) => ({
      // Support both ParameterName (API format) and Name
      parameterName: (e.ParameterName as string) || (e.Name as string) || "",
      // Support SelectedValues.Value (primary), SelectedValues.string (legacy), and Values.string (legacy)
      selectedValues: (() => {
        const selectedValues = e.SelectedValues as Record<string, unknown>
        const values =
          selectedValues?.Value || selectedValues?.string || (e.Values as Record<string, unknown>)?.string
        if (Array.isArray(values)) {
          return values as string[]
        }
        if (values !== undefined && values !== null) {
          return [String(values)]
        }
        return []
      })(),
    }))

    return normalizeEntries(mappedEntries)
  } catch (error) {
    console.error("Failed to deserialize parameters XML:", error)
    return []
  }
}
